<style scoped>
.br-10 {
  padding: 10px;
  border-radius: 10px;
}
</style>

<template>
  <v-container fluid fill-height row>
    <v-layout justify-center align-start>
      <v-flex xl4 lg6 md7 sm10 xs12>
        <v-form @submit.prevent="submitForm($event)">
          <v-card class="br-10 text-center elevation-3">
            <v-layout class="pa-3" column wrap>
              <span class="title primary--text text--darken-2">
                <strong>{{ $t('feedback_form') }}</strong>
              </span>
              <v-text-field
                prepend-icon="fa-user-circle fa-lg"
                :error-messages="errors.collect('subject')"
                clearable
                :value="data.subject"
                @input="patchData({ subject: $event })"
                :label="$t('subject')"
                name="subject"
                required
                type="text"
                v-validate="rules.subject"
              ></v-text-field>
              <v-textarea
                prepend-icon="fa-file fa-lg"
                :error-messages="errors.collect('description')"
                :data-vv-as="$t('description')"
                :label="$t('description')"
                :value="data.description"
                @input="patchData({ description: $event })"
                name="description"
                v-validate="rules.description"
                type="text"
                required
              ></v-textarea>
              <br>
            </v-layout>
          </v-card>
          <br>
          <v-card class="br-10 text-xs-center elevation-3">
            <v-layout class="pa-3" column wrap>
              <span class="title primary--text text--darken-2">
                <strong>{{ $t('user_details') }}</strong>
              </span>
              <v-text-field
                prepend-icon="fa-user-circle fa-lg"
                :error-messages="errors.collect('full_name')"
                clearable
                :value="data.complainant.full_name"
                @input="patchComplainant({ full_name: $event })"
                :label="$t('full_name')"
                name="full_name"
                required
                type="text"
                :disabled="hasSession"
                v-validate="rules.full_name"
              ></v-text-field>
              <v-text-field
                class="pt-2"
                prepend-icon="fa-id-card fa-lg"
                :error-messages="errors.collect('ic_num')"
                clearable
                :data-vv-as="$t('ic_num')"
                :mask="'######-##-####'"
                name="ic_num"
                :value="data.complainant.ic_num"
                :counter="14"
                @input="patchComplainant({ ic_num: $event })"
                :label="$t('ic_num')"
                maxlength="14"
                required
                type="text"
                :disabled="hasSession"
                return-masked-value
                v-validate="rules.ic_no"
              ></v-text-field>
              <v-text-field
                class="pt-2"
                prepend-icon="fa-phone-square fa-lg"
                :error-messages="errors.collect('phone_no')"
                :data-vv-as="$t('phone_no')"
                :label="$t('phone_no')"
                :value="data.complainant.phone"
                @input="patchComplainant({ phone: $event })"
                name="phone_no"
                v-validate="rules.phone"
                maxlength="11"
                type="tel"
                :disabled="hasSession"
                required
              ></v-text-field>
              <v-text-field
                class="pt-2"
                prepend-icon="fa-envelope fa-lg"
                :error-messages="errors.collect('email_address')"
                :data-vv-as="$t('email_address')"
                :value="data.complainant.email"
                @input="patchComplainant({ email: $event })"
                :label="$t('email_address')"
                name="email_address"
                required
                type="email"
                :disabled="hasSession"
                v-validate="rules.email_address"
              ></v-text-field>
            </v-layout>
          </v-card>
          <v-layout justify-end mt-2>
            <v-btn
              :block="$vuetify.breakpoint.xsOnly"
              color="primary"
              type="submit"
              :disabled="isLoading"
              :loading="isLoading"
            >{{ $t('submit') }}</v-btn>
          </v-layout>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  beforeRouteLeave (to, from, next) {
    this.resetData()
    next()
  },
  computed: {
    ...mapGetters({
      hasName: 'auth/hasName',
      hasSession: 'auth/hasSession',
      isLoading: 'feedback/isLoading'
    }),
    ...mapState({
      data: state => state.feedback.data,
      rules: state => state.feedback.rules
    }),
    isValid: function () {
      return Object.keys(this.fields).every(key => this.fields[key].valid)
    }
  },
  created: async function () {
    await this.resetData()
    const fetchSession = this.$store.dispatch('auth/session')
    navigator.geolocation.getCurrentPosition(
      function (position) {
        const geolocation = {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            altitude: position.coords.altitude,
            accuracy: position.coords.accuracy,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed
          },
          timestamp: position.timestamp
        }
        this.$store.dispatch('feedback/patchData', { geolocation })
      }.bind(this),
      error => {
        alert(error.message)
      },
      { enableHighAccuracy: true }
    )
    await fetchSession
      .then(
        function (value) {
          this.$store.commit('loading/setPageLoading', { loading: false })
          if (
            value.userCtx.name !== null &&
            value.userCtx.name !== 'couchdb@menggaris.com'
          ) {
            this.$store.dispatch('feedback/patchComplainant', {
              email: value.userCtx.name
            })
            this.$store.dispatch('feedback/patchComplainant', {
              phone: value.userCtx.phone
            })
            this.$store.dispatch('feedback/patchComplainant', {
              full_name: value.userCtx.full_name
            })
            this.$store.dispatch('feedback/patchComplainant', {
              ic_num: value.userCtx.ic_num
            })
          }
        }.bind(this)
      )
      .catch(function (error) {
        return error
      })
  },
  methods: {
    ...mapActions({
      patchComplainant: 'feedback/patchComplainant',
      patchData: 'feedback/patchData',
      resetData: 'feedback/resetData',
      submitData: 'feedback/submitData',
      updateData: 'feedback/updateData'
    }),
    submitForm: async function ($event) {
      if (!(await this.$validator.validateAll())) return
      const response = await this.submitData()
      this.resetData()
      this.$router.push(`/feedback/detail/${response.id || response._id}?feedback=true`)
    }
  }
}
</script>
